import React, { useEffect, useRef } from 'react';
import { Drawer, Tabs, Table, Tag, Button, Space } from 'antd';
import {
  ClearOutlined,
  CodeOutlined,
  DisconnectOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useDevConsoleStore, LogEntry, NetworkEntry, genId } from '../store/devConsoleStore';
import './DevConsole.css';

const statusColor = (status?: number) => {
  if (!status) return 'default';
  if (status < 300) return 'success';
  if (status < 400) return 'warning';
  return 'error';
};

const levelColor: Record<string, string> = {
  DEBUG: '#8c8c8c',
  INFO: '#1890ff',
  WARNING: '#faad14',
  ERROR: '#ff4d4f',
  CRITICAL: '#cf1322',
};

const NetworkTab: React.FC = () => {
  const { networkEntries, clearNetwork } = useDevConsoleStore();

  const columns = [
    {
      title: 'Method',
      dataIndex: 'method',
      width: 80,
      render: (m: string) => <Tag color="blue">{m}</Tag>,
    },
    {
      title: 'URL',
      dataIndex: 'url',
      ellipsis: true,
      render: (url: string) => {
        const short = url.replace(/^https?:\/\/[^/]+/, '');
        return <span className="dev-console-url">{short || url}</span>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 80,
      render: (s?: number) =>
        s ? <Tag color={statusColor(s)}>{s}</Tag> : <Tag>pending</Tag>,
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      width: 90,
      render: (d?: number) => (d != null ? `${d}ms` : '-'),
    },
  ];

  return (
    <div className="dev-console-tab-content">
      <div className="dev-console-toolbar">
        <Button size="small" icon={<ClearOutlined />} onClick={clearNetwork}>
          清空
        </Button>
        <span className="dev-console-count">{networkEntries.length} 条请求</span>
      </div>
      <Table
        dataSource={networkEntries}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: 280 }}
        expandable={{
          expandedRowRender: (record: NetworkEntry) => (
            <div className="dev-console-detail">
              {record.requestBody && (
                <div>
                  <strong>Request Body:</strong>
                  <pre>{JSON.stringify(record.requestBody, null, 2)}</pre>
                </div>
              )}
              {record.responseBody && (
                <div>
                  <strong>Response Body:</strong>
                  <pre>{JSON.stringify(record.responseBody, null, 2)}</pre>
                </div>
              )}
              {record.error && (
                <div>
                  <strong>Error:</strong> <span style={{ color: '#ff4d4f' }}>{record.error}</span>
                </div>
              )}
            </div>
          ),
        }}
      />
    </div>
  );
};

const LogsTab: React.FC = () => {
  const { logEntries, clearLogs } = useDevConsoleStore();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logEntries.length]);

  return (
    <div className="dev-console-tab-content">
      <div className="dev-console-toolbar">
        <Button size="small" icon={<ClearOutlined />} onClick={clearLogs}>
          清空
        </Button>
        <span className="dev-console-count">{logEntries.length} 条日志</span>
      </div>
      <div className="dev-console-logs">
        {logEntries.map((entry: LogEntry) => (
          <div key={entry.id} className="dev-console-log-line">
            <span
              className="dev-console-log-level"
              style={{ color: levelColor[entry.level] || '#8c8c8c' }}
            >
              [{entry.level}]
            </span>
            <span className="dev-console-log-time">
              {new Date(entry.timestamp * 1000).toLocaleTimeString()}
            </span>
            <span className="dev-console-log-msg">{entry.message}</span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

const DevConsole: React.FC = () => {
  const { visible, activeTab, toggle, setVisible, setActiveTab, wsConnected, setWsConnected, addLogEntry } =
    useDevConsoleStore();
  const wsRef = useRef<WebSocket | null>(null);
  const isTauri = !!(window as any).__TAURI__;

  useEffect(() => {
    if (!isTauri) return;
    const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api\/v1$/, '') || window.location.origin;

    let reconnectTimer: ReturnType<typeof setTimeout>;
    let ws: WebSocket;

    const connect = () => {
      const token = localStorage.getItem('token') || '';
      if (!token) {
        reconnectTimer = setTimeout(connect, 3000);
        return;
      }
      const wsUrl = baseUrl.replace(/^http/, 'ws') + '/ws/dev-console?token=' + encodeURIComponent(token);
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'log') {
            addLogEntry({
              id: genId(),
              level: data.level,
              logger: data.logger,
              message: data.message,
              timestamp: data.timestamp,
            });
          } else if (data.type === 'request') {
            // Server-side request logs also show in logs tab
            addLogEntry({
              id: genId(),
              level: 'INFO',
              logger: 'http',
              message: `${data.method} ${data.path} → ${data.status} (${data.duration_ms}ms)`,
              timestamp: data.timestamp,
            });
          }
        } catch {}
      };
    };

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      ws?.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setWsConnected, addLogEntry]);

  useEffect(() => {
    if (!isTauri) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F12') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggle]);

  if (!isTauri) return null;

  return (
    <Drawer
      title={
        <Space>
          <CodeOutlined />
          <span>DevConsole</span>
          {wsConnected ? (
            <Tag icon={<LinkOutlined />} color="success">已连接</Tag>
          ) : (
            <Tag icon={<DisconnectOutlined />} color="error">未连接</Tag>
          )}
        </Space>
      }
      placement="bottom"
      height={420}
      open={visible}
      onClose={() => setVisible(false)}
      className="dev-console-drawer"
      destroyOnHidden={false}
      zIndex={10001}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k as 'network' | 'logs')}
        items={[
          { key: 'network', label: 'Network', children: <NetworkTab /> },
          { key: 'logs', label: 'Server Logs', children: <LogsTab /> },
        ]}
      />
    </Drawer>
  );
};

export default DevConsole;
